import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
} from "recharts";
import { getToken } from "firebase/messaging";
import { messaging } from "../firebaseConfig";
import { API_URL } from "../config";

/* ============================================================
   DESIGN TOKENS
   Same family as the SilverJarz invoice branding (deep navy +
   brass gold), extended with a cool neutral background instead
   of pure white/gray so the colored charts have room to breathe.
   ============================================================ */
const colors = {
  ink: "#12222c",
  navy: "#1f3a4d",
  navyDeep: "#14232c",
  gold: "#c9a227",
  goldSoft: "#e8d9a8",
  success: "#3f7d58",
  warning: "#b8863b",
  danger: "#b5493f",
  textMuted: "#64707a",
  textFaint: "#9aa5ad",
  border: "#e4e7eb",
  borderLight: "#edeff1",
  bgPage: "#f4f5f7",
  bgCard: "#ffffff",
  bgSubtle: "#f8f9fa",
};

const STATUS_COLORS: Record<string, string> = {
  Pending: colors.warning,
  Confirmed: colors.navy,
  Completed: colors.success,
  Cancelled: colors.danger,
};

const SERVICE_COLORS = [colors.navy, colors.gold, "#7a94a3"];

const TYPE_META: Record<string, { color: string; icon: string }> = {
  "Car Rental": { color: "#7a94a3", icon: "fa-car" },
  "Flight Request": { color: colors.navy, icon: "fa-plane" },
  "Dummy Ticket": { color: colors.gold, icon: "fa-ticket-alt" },
  "Visa Application": { color: colors.success, icon: "fa-passport" },
  Activity: { color: colors.warning, icon: "fa-hiking" },
};

/* ============================================================
   TYPES — mirrors DashboardController::overview()
   ============================================================ */
interface TrendPoint {
  label: string;
  value: number;
}
interface Kpi {
  total: number;
  trend: TrendPoint[];
  growth_percent: number;
}
interface RevenueKpi extends Kpi {
  pending: number;
  overdue: number;
  currency: string;
}
interface RevenuePoint {
  month: string;
  revenue: number;
}
interface ServiceSplit {
  name: string;
  value: number;
}
interface StatusBreakdown {
  status: string;
  count: number;
}
interface ActivityItem {
  type: string;
  icon: string;
  title: string;
  subtitle: string;
  status: string;
  created_at: string;
}
interface Destination {
  destination: string;
  count: number;
}
interface OverviewData {
  kpis: {
    clients: Kpi;
    trips: Kpi;
    visa_applications: Kpi;
    car_bookings: Kpi;
    flight_requests: Kpi;
    dummy_tickets: Kpi;
    activity_bookings: Kpi;
    revenue: RevenueKpi;
  };
  revenue_trend: RevenuePoint[];
  service_split: ServiceSplit[];
  status_breakdown: StatusBreakdown[];
  recent_activity: ActivityItem[];
  top_destinations: Destination[];
}

/* ============================================================
   SKELETON COMPONENTS
   ============================================================ */
function SkeletonCard() {
  return (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.borderLight}`,
        borderRadius: 14,
        padding: "18px 18px 14px 18px",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        minWidth: 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div style={{ minWidth: 0, width: "100%" }}>
          <div style={{ 
            width: "60%", 
            height: 12, 
            background: "#e0e3e6", 
            borderRadius: 4,
            animation: "skeleton-pulse 1.5s ease-in-out infinite"
          }} />
          <div style={{ 
            width: "80%", 
            height: 28, 
            background: "#e0e3e6", 
            borderRadius: 4, 
            marginTop: 6,
            animation: "skeleton-pulse 1.5s ease-in-out infinite 0.2s"
          }} />
        </div>
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 9,
            background: "#e0e3e6",
            flexShrink: 0,
            animation: "skeleton-pulse 1.5s ease-in-out infinite 0.4s"
          }}
        />
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div style={{ 
          width: 60, 
          height: 24, 
          background: "#e0e3e6", 
          borderRadius: 20,
          animation: "skeleton-pulse 1.5s ease-in-out infinite 0.3s"
        }} />
        <div style={{ width: 64, height: 24, background: "#e0e3e6", borderRadius: 4 }} />
      </div>
    </div>
  );
}

function SkeletonSectionCard() {
  return (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.borderLight}`,
        borderRadius: 14,
        padding: 22,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 18 }}>
        <div style={{ width: "100%" }}>
          <div style={{ 
            width: "40%", 
            height: 20, 
            background: "#e0e3e6", 
            borderRadius: 4,
            animation: "skeleton-pulse 1.5s ease-in-out infinite"
          }} />
          <div style={{ 
            width: "30%", 
            height: 14, 
            background: "#e0e3e6", 
            borderRadius: 4, 
            marginTop: 4,
            animation: "skeleton-pulse 1.5s ease-in-out infinite 0.2s"
          }} />
        </div>
      </div>
      <div style={{ 
        width: "100%", 
        height: 200, 
        background: "#e0e3e6", 
        borderRadius: 8,
        animation: "skeleton-pulse 1.5s ease-in-out infinite 0.3s"
      }} />
    </div>
  );
}

function SkeletonStatusBar() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
      <div style={{ 
        width: 78, 
        height: 14, 
        background: "#e0e3e6", 
        borderRadius: 4,
        animation: "skeleton-pulse 1.5s ease-in-out infinite"
      }} />
      <div style={{ flex: 1, height: 9, background: "#edeff1", borderRadius: 6, overflow: "hidden" }}>
        <div style={{ 
          width: "60%", 
          height: "100%", 
          background: "#e0e3e6", 
          borderRadius: 6,
          animation: "skeleton-pulse 1.5s ease-in-out infinite 0.2s"
        }} />
      </div>
      <div style={{ 
        width: 28, 
        height: 16, 
        background: "#e0e3e6", 
        borderRadius: 4,
        animation: "skeleton-pulse 1.5s ease-in-out infinite 0.3s"
      }} />
    </div>
  );
}

function SkeletonTimelineItem() {
  return (
    <div style={{ display: "flex", gap: 12, padding: "9px 8px", borderRadius: 8 }}>
      <div style={{ 
        width: 32, 
        height: 32, 
        borderRadius: "50%", 
        background: "#e0e3e6",
        flexShrink: 0,
        animation: "skeleton-pulse 1.5s ease-in-out infinite"
      }} />
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div style={{ 
            width: "50%", 
            height: 16, 
            background: "#e0e3e6", 
            borderRadius: 4,
            animation: "skeleton-pulse 1.5s ease-in-out infinite 0.2s"
          }} />
          <div style={{ 
            width: "20%", 
            height: 14, 
            background: "#e0e3e6", 
            borderRadius: 4,
            animation: "skeleton-pulse 1.5s ease-in-out infinite 0.4s"
          }} />
        </div>
        <div style={{ 
          width: "40%", 
          height: 14, 
          background: "#e0e3e6", 
          borderRadius: 4, 
          marginTop: 4,
          animation: "skeleton-pulse 1.5s ease-in-out infinite 0.3s"
        }} />
        <div style={{ 
          width: "30%", 
          height: 12, 
          background: "#e0e3e6", 
          borderRadius: 4, 
          marginTop: 4,
          animation: "skeleton-pulse 1.5s ease-in-out infinite 0.5s"
        }} />
      </div>
    </div>
  );
}

function SkeletonDestination() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0" }}>
      <div style={{ 
        width: 20, 
        height: 20, 
        borderRadius: "50%", 
        background: "#e0e3e6",
        animation: "skeleton-pulse 1.5s ease-in-out infinite"
      }} />
      <div style={{ 
        flex: 1, 
        height: 14, 
        background: "#e0e3e6", 
        borderRadius: 4,
        animation: "skeleton-pulse 1.5s ease-in-out infinite 0.2s"
      }} />
      <div style={{ 
        width: 40, 
        height: 14, 
        background: "#e0e3e6", 
        borderRadius: 4,
        animation: "skeleton-pulse 1.5s ease-in-out infinite 0.4s"
      }} />
    </div>
  );
}

function GrowthBadge({ value }: { value: number }) {
  const positive = value >= 0;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        fontSize: 11.5,
        fontWeight: 700,
        color: positive ? colors.success : colors.danger,
        background: positive ? "#e9f3ed" : "#fbeceb",
        padding: "2px 7px",
        borderRadius: 20,
      }}
    >
      <i className={`fas fa-arrow-${positive ? "up" : "down"}`} style={{ fontSize: 9 }} />
      {Math.abs(value).toFixed(1)}%
    </span>
  );
}

function KpiCard({
  title,
  value,
  icon,
  accentColor,
  growth,
  trend,
  isCurrency,
}: {
  title: string;
  value: number;
  icon: string;
  accentColor: string;
  growth: number;
  trend: TrendPoint[];
  isCurrency?: boolean;
}) {
  return (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.borderLight}`,
        borderRadius: 14,
        padding: "18px 18px 14px 18px",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        minWidth: 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11.5, fontWeight: 600, color: colors.textMuted, letterSpacing: 0.3, whiteSpace: "nowrap" }}>
            {title}
          </div>
          <div
            style={{
              fontSize: 24,
              fontWeight: 700,
              color: colors.ink,
              marginTop: 4,
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {isCurrency ? `${value.toLocaleString()} OMR` : value.toLocaleString()}
          </div>
        </div>
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 9,
            background: `${accentColor}17`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <i className={`fas ${icon}`} style={{ color: accentColor, fontSize: 14 }} />
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <GrowthBadge value={growth} />
        <div style={{ width: 64, height: 24 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend}>
              <Line type="monotone" dataKey="value" stroke={accentColor} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function SectionCard({
  title,
  subtitle,
  action,
  children,
  style,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.borderLight}`,
        borderRadius: 14,
        padding: 22,
        ...style,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 18 }}>
        <div>
          <h2 style={{ fontFamily: "Georgia, serif", fontSize: 16.5, fontWeight: 700, margin: 0, color: colors.ink }}>
            {title}
          </h2>
          {subtitle && (
            <p style={{ fontSize: 12, color: colors.textMuted, margin: "3px 0 0 0" }}>{subtitle}</p>
          )}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function StatusBar({ item, max }: { item: StatusBreakdown; max: number }) {
  const pct = max > 0 ? (item.count / max) * 100 : 0;
  const color = STATUS_COLORS[item.status] ?? colors.navy;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
      <div style={{ width: 78, fontSize: 12, color: colors.textMuted, fontWeight: 600 }}>{item.status}</div>
      <div style={{ flex: 1, background: colors.bgSubtle, borderRadius: 6, height: 9, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, background: color, height: "100%", borderRadius: 6, transition: "width 0.6s ease" }} />
      </div>
      <div style={{ width: 28, textAlign: "right", fontSize: 12.5, fontWeight: 700, color: colors.ink }}>{item.count}</div>
    </div>
  );
}

function timeAgoColor(status: string) {
  const s = status.toLowerCase();
  if (["cancelled", "rejected"].includes(s)) return colors.danger;
  if (["paid", "completed", "delivered", "approved"].includes(s)) return colors.success;
  if (["confirmed", "embassy_processing", "awaiting_payment"].includes(s)) return colors.navy;
  return colors.warning;
}

/* ============================================================
   MAIN DASHBOARD
   ============================================================ */
const Dashboard = () => {
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchOverview = async () => {
    setLoading(true);
    setError(false);
    try {
      const token = localStorage.getItem("token_admin_silverjarz");
      const response = await axios.get(`${API_URL}/admin/dashboard/overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.status) {
        setData(response.data.data);
      } else {
        setError(true);
      }
    } catch (err) {
      console.error("Error fetching dashboard overview:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverview();
  }, []);

  // Push notification registration — unchanged from the previous dashboard.
  useEffect(() => {
    const registerFcmToken = async () => {
      try {
        const permission = await Notification.requestPermission();
        if (permission === "granted") {
          const currentToken = await getToken(messaging, {
            vapidKey: "BL-NbcJ36OObsXhBUEld-xvTpSj_z1048JQY5ZtpAAEJEgCjZafCbc0CcqlHA6IaMoxTpxMONMasNsYv7z3oMvg",
          });
          if (currentToken) {
            const jwtToken = localStorage.getItem("token_admin_silverjarz");
            await fetch(`${API_URL}/fcm-token`, {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwtToken}` },
              body: JSON.stringify({ fcm_token: currentToken }),
            });
          }
        }
      } catch (err) {
        console.error("Error retrieving FCM token:", err);
      }
    };
    registerFcmToken();
  }, []);

  // Loading Skeleton
  if (loading) {
    return (
      <div style={{ width: "100%", minHeight: "100vh", background: colors.bgPage, padding: "24px", boxSizing: "border-box" }}>
        <style>{`
          @keyframes skeleton-pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}</style>

        {/* HEADER SKELETON */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ 
              width: 200, 
              height: 32, 
              background: "#e0e3e6", 
              borderRadius: 4,
              animation: "skeleton-pulse 1.5s ease-in-out infinite"
            }} />
            <div style={{ 
              width: 350, 
              height: 16, 
              background: "#e0e3e6", 
              borderRadius: 4, 
              marginTop: 4,
              animation: "skeleton-pulse 1.5s ease-in-out infinite 0.2s"
            }} />
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ 
              width: 120, 
              height: 40, 
              background: "#e0e3e6", 
              borderRadius: 9,
              animation: "skeleton-pulse 1.5s ease-in-out infinite 0.3s"
            }} />
            <div style={{ 
              width: 140, 
              height: 40, 
              background: "#e0e3e6", 
              borderRadius: 9,
              animation: "skeleton-pulse 1.5s ease-in-out infinite 0.4s"
            }} />
          </div>
        </div>

        {/* KPI STRIP SKELETON */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 14, marginBottom: 16 }}>
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>

        {/* ANALYTICS ROW SKELETON */}
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.7fr) minmax(0, 1fr)", gap: 16, marginBottom: 16 }}>
          <SkeletonSectionCard />
          <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
            <SkeletonSectionCard />
            <div
              style={{
                background: colors.bgCard,
                border: `1px solid ${colors.borderLight}`,
                borderRadius: 14,
                padding: 22,
                flex: 1,
              }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 18 }}>
                <div>
                  <div style={{ 
                    width: "50%", 
                    height: 20, 
                    background: "#e0e3e6", 
                    borderRadius: 4,
                    animation: "skeleton-pulse 1.5s ease-in-out infinite"
                  }} />
                  <div style={{ 
                    width: "40%", 
                    height: 14, 
                    background: "#e0e3e6", 
                    borderRadius: 4, 
                    marginTop: 4,
                    animation: "skeleton-pulse 1.5s ease-in-out infinite 0.2s"
                  }} />
                </div>
              </div>
              <SkeletonStatusBar />
              <SkeletonStatusBar />
              <SkeletonStatusBar />
              <SkeletonStatusBar />
            </div>
          </div>
        </div>

        {/* ACTIVITY + DESTINATIONS ROW SKELETON */}
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.7fr) minmax(0, 1fr)", gap: 16 }}>
          <div
            style={{
              background: colors.bgCard,
              border: `1px solid ${colors.borderLight}`,
              borderRadius: 14,
              padding: 22,
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 18 }}>
              <div>
                <div style={{ 
                  width: "40%", 
                  height: 20, 
                  background: "#e0e3e6", 
                  borderRadius: 4,
                  animation: "skeleton-pulse 1.5s ease-in-out infinite"
                }} />
                <div style={{ 
                  width: "30%", 
                  height: 14, 
                  background: "#e0e3e6", 
                  borderRadius: 4, 
                  marginTop: 4,
                  animation: "skeleton-pulse 1.5s ease-in-out infinite 0.2s"
                }} />
              </div>
            </div>
            <div style={{ position: "relative", maxHeight: 380 }}>
              <div style={{ position: "absolute", left: 15, top: 6, bottom: 6, width: 2, background: colors.borderLight }} />
              <SkeletonTimelineItem />
              <SkeletonTimelineItem />
              <SkeletonTimelineItem />
              <SkeletonTimelineItem />
              <SkeletonTimelineItem />
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
            <div
              style={{
                background: colors.bgCard,
                border: `1px solid ${colors.borderLight}`,
                borderRadius: 14,
                padding: 22,
              }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 18 }}>
                <div>
                  <div style={{ 
                    width: "50%", 
                    height: 20, 
                    background: "#e0e3e6", 
                    borderRadius: 4,
                    animation: "skeleton-pulse 1.5s ease-in-out infinite"
                  }} />
                  <div style={{ 
                    width: "40%", 
                    height: 14, 
                    background: "#e0e3e6", 
                    borderRadius: 4, 
                    marginTop: 4,
                    animation: "skeleton-pulse 1.5s ease-in-out infinite 0.2s"
                  }} />
                </div>
              </div>
              <SkeletonDestination />
              <SkeletonDestination />
              <SkeletonDestination />
              <SkeletonDestination />
              <SkeletonDestination />
            </div>

            <div
              style={{
                background: colors.bgCard,
                border: `1px solid ${colors.borderLight}`,
                borderRadius: 14,
                padding: 22,
                flex: 1,
              }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 18 }}>
                <div>
                  <div style={{ 
                    width: "40%", 
                    height: 20, 
                    background: "#e0e3e6", 
                    borderRadius: 4,
                    animation: "skeleton-pulse 1.5s ease-in-out infinite"
                  }} />
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ 
                  width: "100%", 
                  height: 40, 
                  background: "#e0e3e6", 
                  borderRadius: 8,
                  animation: "skeleton-pulse 1.5s ease-in-out infinite"
                }} />
                <div style={{ 
                  width: "100%", 
                  height: 40, 
                  background: "#e0e3e6", 
                  borderRadius: 8,
                  animation: "skeleton-pulse 1.5s ease-in-out infinite 0.2s"
                }} />
                <div style={{ 
                  width: "100%", 
                  height: 40, 
                  background: "#e0e3e6", 
                  borderRadius: 8,
                  animation: "skeleton-pulse 1.5s ease-in-out infinite 0.4s"
                }} />
                <div style={{ 
                  width: "100%", 
                  height: 40, 
                  background: "#e0e3e6", 
                  borderRadius: 8,
                  animation: "skeleton-pulse 1.5s ease-in-out infinite 0.6s"
                }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ width: "100%", minHeight: "100vh", background: colors.bgPage, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <p style={{ color: colors.textMuted, fontSize: 13, marginBottom: 12 }}>
            Unable to load dashboard statistics.
          </p>
          <button
            onClick={fetchOverview}
            style={{ padding: "8px 16px", background: colors.navy, color: "#fff", border: "none", borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { kpis, revenue_trend, service_split, status_breakdown, recent_activity, top_destinations } = data;
  const maxStatusCount = Math.max(...status_breakdown.map((s) => s.count), 1);
  const totalServiceValue = service_split.reduce((sum, s) => sum + s.value, 0);

  return (
    <div style={{ width: "100%", minHeight: "100vh", background: colors.bgPage, padding: "24px", boxSizing: "border-box" }}>
      <style>{`
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .db-fade { animation: fadeInUp 0.35s ease both; }
        .db-timeline-item:hover { background: ${colors.bgSubtle}; }
      `}</style>

      {/* HEADER */}
      <div className="db-fade" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: "Georgia, serif", fontSize: 24, fontWeight: 700, margin: 0, color: colors.ink }}>
            Dashboard
          </h1>
          <p style={{ fontSize: 13, color: colors.textMuted, margin: "4px 0 0 0" }}>
            Overview of visas, flights, rentals, and activities — SilverJarz Oman.
          </p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <Link
            to="/admin/list-invoices"
            style={{ padding: "9px 16px", background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 9, fontSize: 12.5, fontWeight: 600, color: colors.ink, textDecoration: "none" }}
          >
            View Invoices
          </Link>
          <Link
            to="/admin/add-invoice"
            style={{ padding: "9px 16px", background: colors.navy, borderRadius: 9, fontSize: 12.5, fontWeight: 600, color: "#fff", textDecoration: "none" }}
          >
            + New Invoice
          </Link>
        </div>
      </div>

      {/* KPI STRIP */}
      <div
        className="db-fade"
        style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 14, marginBottom: 16 }}
      >
        <KpiCard title="Revenue (paid)" value={kpis.revenue.total} icon="fa-sack-dollar" accentColor={colors.gold} growth={kpis.revenue.growth_percent} trend={kpis.revenue.trend} isCurrency />
        <KpiCard title="Clients" value={kpis.clients.total} icon="fa-users" accentColor={colors.navy} growth={kpis.clients.growth_percent} trend={kpis.clients.trend} />
        <KpiCard title="Visas" value={kpis.visa_applications.total} icon="fa-passport" accentColor={colors.success} growth={kpis.visa_applications.growth_percent} trend={kpis.visa_applications.trend} />
        <KpiCard title="Flight Requests" value={kpis.flight_requests.total} icon="fa-plane" accentColor={colors.navy} growth={kpis.flight_requests.growth_percent} trend={kpis.flight_requests.trend} />
        <KpiCard title="Car Rentals" value={kpis.car_bookings.total} icon="fa-car" accentColor="#7a94a3" growth={kpis.car_bookings.growth_percent} trend={kpis.car_bookings.trend} />
        <KpiCard title="Dummy Tickets" value={kpis.dummy_tickets.total} icon="fa-ticket-alt" accentColor={colors.gold} growth={kpis.dummy_tickets.growth_percent} trend={kpis.dummy_tickets.trend} />
      </div>

      {/* ANALYTICS ROW */}
      <div className="db-fade" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.7fr) minmax(0, 1fr)", gap: 16, marginBottom: 16 }}>
        {/* Revenue trend */}
        <SectionCard title="Revenue Trend" subtitle="Paid invoices, last 12 months">
          <div style={{ width: "100%", height: 230 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenue_trend} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={colors.navy} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={colors.navy} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: colors.textMuted }} axisLine={{ stroke: colors.borderLight }} tickLine={false} />
                <Tooltip
                  formatter={(value: any) => [`${(value as number).toLocaleString()} OMR`, "Revenue"]}
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: `1px solid ${colors.border}` }}
                />
                <Area type="monotone" dataKey="revenue" stroke={colors.navy} strokeWidth={2.5} fill="url(#revenueFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        {/* Right column: service split + status */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
          <SectionCard title="Service Distribution" subtitle="Share of billed revenue">
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ width: 96, height: 96, flexShrink: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={service_split} dataKey="value" nameKey="name" innerRadius={30} outerRadius={46} paddingAngle={3}>
                      {service_split.map((_, i) => (
                        <Cell key={i} fill={SERVICE_COLORS[i % SERVICE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name) => {
                        const numericValue = typeof value === 'number' ? value : 0;
                        return `${numericValue.toLocaleString()} OMR`;
                      }} 
                      contentStyle={{ fontSize: 11.5, borderRadius: 8 }} 
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
                {service_split.map((s, i) => {
                  const pct = totalServiceValue > 0 ? Math.round((s.value / totalServiceValue) * 100) : 0;
                  return (
                    <div key={s.name} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: SERVICE_COLORS[i % SERVICE_COLORS.length], flexShrink: 0 }} />
                      <span style={{ color: colors.textMuted }}>{s.name}</span>
                      <span style={{ fontWeight: 700, color: colors.ink, marginLeft: "auto" }}>{pct}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Booking Statuses" subtitle="All services combined" style={{ flex: 1 }}>
            {status_breakdown.map((s) => (
              <StatusBar key={s.status} item={s} max={maxStatusCount} />
            ))}
          </SectionCard>
        </div>
      </div>

      {/* ACTIVITY + DESTINATIONS ROW */}
      <div className="db-fade" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.7fr) minmax(0, 1fr)", gap: 16 }}>
        {/* Live activity timeline — the signature element: every service
            line merged into one reverse-chronological feed. */}
        <SectionCard
          title="Recent Activity"
          subtitle="Latest actions, all services"
          action={
            <span style={{ fontSize: 11, color: colors.textFaint, fontWeight: 600 }}>
              <i className="fas fa-circle" style={{ fontSize: 6, color: colors.success, marginRight: 5 }} />
              live
            </span>
          }
        >
          <div style={{ position: "relative", maxHeight: 380, overflowY: "auto" }}>
            <div style={{ position: "absolute", left: 15, top: 6, bottom: 6, width: 2, background: colors.borderLight }} />
            {recent_activity.map((item, i) => {
              const meta = TYPE_META[item.type] ?? { color: colors.navy, icon: "fa-circle" };
              return (
                <div
                  key={i}
                  className="db-timeline-item"
                  style={{ display: "flex", gap: 12, padding: "9px 8px", borderRadius: 8, position: "relative" }}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      background: `${meta.color}17`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      zIndex: 1,
                    }}
                  >
                    <i className={`fas ${meta.icon}`} style={{ color: meta.color, fontSize: 12 }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: colors.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {item.title}
                      </span>
                      <span style={{ fontSize: 11, color: colors.textFaint, flexShrink: 0 }}>{item.created_at}</span>
                    </div>
                    <div style={{ fontSize: 11.5, color: colors.textMuted, marginTop: 1 }}>{item.subtitle}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
                      <span style={{ fontSize: 10.5, fontWeight: 700, color: meta.color }}>{item.type}</span>
                      <span style={{ fontSize: 10, color: colors.textFaint }}>•</span>
                      <span style={{ fontSize: 10.5, fontWeight: 600, color: timeAgoColor(item.status) }}>
                        {item.status.replace(/_/g, " ")}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
            {recent_activity.length === 0 && (
              <p style={{ fontSize: 12.5, color: colors.textFaint, textAlign: "center", padding: "24px 0" }}>
                No recent activity.
              </p>
            )}
          </div>
        </SectionCard>

        {/* Right sidebar: top destinations + quick actions */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
          <SectionCard title="Top Destinations" subtitle="Flights, last 90 days">
            {top_destinations.length === 0 && (
              <p style={{ fontSize: 12.5, color: colors.textFaint }}>No flight requests yet.</p>
            )}
            {top_destinations.map((d, i) => (
              <div key={d.destination} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", borderBottom: i < top_destinations.length - 1 ? `1px solid ${colors.borderLight}` : "none" }}>
                <span style={{ width: 20, height: 20, borderRadius: "50%", background: colors.bgSubtle, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10.5, fontWeight: 700, color: colors.textMuted, flexShrink: 0 }}>
                  {i + 1}
                </span>
                <span style={{ fontSize: 12.5, color: colors.ink, fontWeight: 600, flex: 1 }}>{d.destination}</span>
                <span style={{ fontSize: 11.5, color: colors.textMuted, fontWeight: 600 }}>{d.count} requests</span>
              </div>
            ))}
          </SectionCard>

          <SectionCard title="Quick Actions" style={{ flex: 1 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Link to="/admin/list-visa-applications" style={quickLinkStyle}>
                <i className="fas fa-passport" style={{ color: colors.success, width: 16 }} /> Visa Applications
              </Link>
              <Link to="/admin/list-flight-requests" style={quickLinkStyle}>
                <i className="fas fa-plane" style={{ color: colors.navy, width: 16 }} /> Flight Requests
              </Link>
              <Link to="/admin/list-booking-car" style={quickLinkStyle}>
                <i className="fas fa-car" style={{ color: "#7a94a3", width: 16 }} /> Car Rentals
              </Link>
              <Link to="/admin/list-clients" style={quickLinkStyle}>
                <i className="fas fa-users" style={{ color: colors.gold, width: 16 }} /> Clients
              </Link>
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
};

const quickLinkStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "9px 10px",
  borderRadius: 8,
  background: colors.bgSubtle,
  border: `1px solid ${colors.borderLight}`,
  fontSize: 12.5,
  fontWeight: 600,
  color: colors.ink,
  textDecoration: "none",
};

export default Dashboard;